import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import {
  MapPin,
  Navigation,
  Building2,
  Crosshair,
  Layers,
  ZoomIn,
  ZoomOut,
  X,
} from "lucide-react";
import { Location } from "../../../types/location";
import "leaflet/dist/leaflet.css";

// Fix default marker icon in Leaflet with Vite
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const INDIA_CENTER: [number, number] = [20.5937, 78.9629];
const INDIA_STATES_GEOJSON = "https://raw.githubusercontent.com/Subhash9325/GeoJson-Data-of-Indian-States/master/Indian_States";

// Haversine distance in km
function haversine(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getCoords(loc: Location): { lat: number; lng: number } | null {
  const lat =
    loc.latitude != null
      ? parseFloat(String(loc.latitude))
      : loc.mainAddress?.coordinates?.latitude;
  const lng =
    loc.longitude != null
      ? parseFloat(String(loc.longitude))
      : loc.mainAddress?.coordinates?.longitude;
  if (
    lat != null &&
    lng != null &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  ) {
    return { lat, lng };
  }
  return null;
}

// Custom icons - inline styles for marker (Tailwind won't process HTML strings)
const markerIcon = (isHighlight: boolean) =>
  L.divIcon({
    className: "!border-0 !bg-transparent",
    html: `<div style="width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.2);${
      isHighlight
        ? "background:#0d9488;outline:3px solid rgba(13,148,136,0.3);transform:scale(1.2)"
        : "background:white;border:2px solid #0d9488"
    }">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="${isHighlight ? "white" : "#0d9488"}"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
    </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

function MapController({
  selectedId,
  locations,
}: {
  selectedId: string | null;
  locations: Location[];
}) {
  const map = useMap();
  const coords = useMemo(() => {
    if (selectedId) {
      const loc = locations.find((l) => (l._id || l.id) === selectedId);
      const c = loc ? getCoords(loc) : null;
      if (c) return [c.lat, c.lng] as [number, number];
    }
    return null;
  }, [selectedId, locations]);

  useEffect(() => {
    if (coords) {
      map.flyTo(coords, 12, { duration: 0.5 });
    }
  }, [coords, map]);

  return null;
}

function ZoomControls() {
  const map = useMap();
  return (
    <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-1 bg-white rounded-lg shadow-md border border-slate-200 p-1">
      <button
        type="button"
        onClick={() => map.zoomIn()}
        className="p-2 hover:bg-slate-50 rounded text-slate-600"
      >
        <ZoomIn className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => map.zoomOut()}
        className="p-2 hover:bg-slate-50 rounded text-slate-600"
      >
        <ZoomOut className="w-4 h-4" />
      </button>
    </div>
  );
}

interface LocationMapViewProps {
  locations: Location[];
  selectedLocation: Location | null;
  onSelectLocation: (loc: Location | null) => void;
  onEdit?: (loc: Location) => void;
  height?: string;
  className?: string;
}

export default function LocationMapView({
  locations,
  selectedLocation,
  onSelectLocation,
  onEdit,
  height = "420px",
  className = "",
}: LocationMapViewProps) {
  const [geoJson, setGeoJson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const locationsWithCoords = useMemo(() => {
    return locations
      .map((loc) => ({ loc, coords: getCoords(loc) }))
      .filter((x): x is { loc: Location; coords: { lat: number; lng: number } } =>
        Boolean(x.coords)
      );
  }, [locations]);

  const nearbyLocations = useMemo(() => {
    if (!selectedLocation) return [];
    const c = getCoords(selectedLocation);
    if (!c) return [];
    return locationsWithCoords
      .filter((x) => x.loc._id !== selectedLocation._id)
      .map((x) => ({
        ...x,
        distance: haversine(c.lat, c.lng, x.coords.lat, x.coords.lng),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }, [selectedLocation, locationsWithCoords]);

  useEffect(() => {
    fetch(INDIA_STATES_GEOJSON)
      .then((r) => r.json())
      .then((data) => setGeoJson(data))
      .catch(() => setGeoJson(null));
  }, []);

  const selectedId = selectedLocation?._id || (selectedLocation?.id as string) || null;
  const selectedCoords = selectedLocation ? getCoords(selectedLocation) : null;

  return (
    <div
      className={`relative bg-slate-50 rounded-xl border border-slate-200 overflow-hidden shadow-sm ${className}`}
      style={{ height }}
    >
      {/* Compact header */}
      <div className="absolute top-0 left-0 right-0 z-[1000] flex items-center justify-between px-3 py-2 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wide">
              State Map & Plant View
            </h3>
            <p className="text-[10px] text-slate-500">
              {locationsWithCoords.length} location{locationsWithCoords.length !== 1 ? "s" : ""} with coordinates
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {selectedLocation && (
            <button
              onClick={() => onSelectLocation(null)}
              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              title="Clear selection"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex h-full pt-12">
        {/* Map */}
        <div className="flex-1 relative min-w-0">
          <MapContainer
            center={INDIA_CENTER}
            zoom={5}
            className="h-full w-full z-0"
            zoomControl={false}
            whenReady={() => setMapReady(true)}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoJson && (
              <GeoJSON
                data={geoJson}
                style={{
                  fillColor: "#0d9488",
                  fillOpacity: 0.08,
                  color: "#0f766e",
                  weight: 1,
                }}
                onEachFeature={(feature, layer) => {
                  const name = feature.properties?.NAME_1;
                  if (name) {
                    layer.bindTooltip(name, {
                      permanent: false,
                      direction: "top",
                      className: "text-[10px] font-medium",
                    });
                  }
                }}
              />
            )}
            {mapReady && (
              <MapController selectedId={selectedId} locations={locations} />
            )}
            <ZoomControls />
            {locationsWithCoords.map(({ loc, coords }) => (
              <Marker
                key={loc._id || String(loc.id)}
                position={[coords.lat, coords.lng]}
                icon={markerIcon(selectedId === (loc._id || loc.id))}
                eventHandlers={{
                  click: () => onSelectLocation(loc),
                }}
              >
                <Popup className="text-xs">
                  <div className="min-w-[160px]">
                    <div className="font-semibold text-slate-800">{loc.name}</div>
                    <div className="text-slate-500 text-[10px] mt-1">
                      {[loc.city || loc.mainAddress?.city, loc.state || loc.mainAddress?.state]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 font-mono">
                      {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                    </div>
                    {onEdit && (
                      <button
                        onClick={() => onEdit(loc)}
                        className="mt-2 text-[10px] text-teal-600 hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Right panel - compact details */}
        <div className="w-64 flex-shrink-0 border-l border-slate-200 bg-white flex flex-col">
          {selectedLocation ? (
            <>
              <div className="p-3 border-b border-slate-100">
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-slate-800 truncate">
                      {selectedLocation.name}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {selectedLocation.type} • {selectedLocation.status}
                    </div>
                  </div>
                </div>
              </div>

              {/* Shared coordinates - plant view */}
              {selectedCoords && (
                <div className="p-3 border-b border-slate-100">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Crosshair className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                      Coordinates
                    </span>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2 font-mono text-[11px] text-slate-700">
                    <div>Lat: {selectedCoords.lat.toFixed(6)}</div>
                    <div>Lng: {selectedCoords.lng.toFixed(6)}</div>
                  </div>
                </div>
              )}

              {/* Nearby locations */}
              <div className="flex-1 overflow-auto p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <Navigation className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                    Nearby Locations
                  </span>
                </div>
                <div className="space-y-1.5">
                  {nearbyLocations.length === 0 ? (
                    <p className="text-[10px] text-slate-400 italic">No other locations nearby</p>
                  ) : (
                    nearbyLocations.map(({ loc, distance }) => (
                      <button
                        key={loc._id || loc.id}
                        onClick={() => onSelectLocation(loc)}
                        className="w-full text-left p-2 rounded-lg bg-slate-50 hover:bg-teal-50 border border-transparent hover:border-teal-100 transition-colors group"
                      >
                        <div className="text-[11px] font-medium text-slate-800 truncate group-hover:text-teal-700">
                          {loc.name}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {distance.toFixed(1)} km away
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {onEdit && (
                <div className="p-3 border-t border-slate-100">
                  <button
                    onClick={() => onEdit(selectedLocation)}
                    className="w-full py-2 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                  >
                    Edit Location
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-6 text-center">
              <div>
                <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-[11px] text-slate-500">
                  Click a marker to view details, coordinates & nearby locations
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
